/**
 * uploadDraftStore — single-record IndexedDB persistence for the Upload Draft.
 *
 * Contract:
 *   • Stores AT MOST one draft under key "current".
 *   • Draft schema: { blob: Blob, name: string, type: string, size: number, createdAt: number }
 *   • Expires after DRAFT_TTL_MS (30 minutes) so a stale draft never surprises a user.
 *   • Never throws in normal use — read failures resolve to null, write failures resolve silently.
 *     The in-memory layer is authoritative; IDB is a safety net for accidental refresh.
 *
 * Why not sessionStorage? Phone photos are 5–15MB. Base64 encoding blocks the main
 * thread and doubles the size. IDB stores Blobs natively and asynchronously.
 */

const DB_NAME = "colourmatch-upload-draft";
const STORE = "draft";
const KEY = "current";
export const DRAFT_TTL_MS = 30 * 60 * 1000; // 30 minutes

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB unavailable"));
      return;
    }
    const req = indexedDB.open(DB_NAME, 1);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        db.createObjectStore(STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function saveDraft({ file }) {
  if (!file) return;
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).put(
        {
          blob: file,
          name: file.name || "photo.jpg",
          type: file.type || "image/jpeg",
          size: file.size,
          createdAt: Date.now(),
        },
        KEY,
      );
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // IDB unavailable (private mode, quota) — silent no-op. In-memory layer remains authoritative.
  }
}

export async function loadDraft() {
  try {
    const db = await openDb();
    const record = await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readonly");
      const req = tx.objectStore(STORE).get(KEY);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => reject(req.error);
    });
    db.close();
    if (!record) return null;
    // Expiry check — refuse to hand back a stale draft.
    if (Date.now() - record.createdAt > DRAFT_TTL_MS) {
      await clearDraft();
      return null;
    }
    // Reconstruct a File so downstream code can treat it identically to a fresh pick.
    const file = new File([record.blob], record.name, {
      type: record.type,
      lastModified: record.createdAt,
    });
    return { file, createdAt: record.createdAt };
  } catch {
    return null;
  }
}

export async function clearDraft() {
  try {
    const db = await openDb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, "readwrite");
      tx.objectStore(STORE).delete(KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
    db.close();
  } catch {
    // silent no-op
  }
}
